import { IconBan } from 'featherico';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { compose, filter, map } from 'lodash/fp';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import AlertBox from '../../../../components/AlertBox';
import Button from '../../../../components/Button';
import { DeprecatedButtonContainer } from '../../../../components/ButtonContainer';
import { Card } from '../../../../components/Cards';
import { FormCheckboxContainer, FormSelect } from '../../../../components/Form/FormInputs';
import { FormItem } from '../../../../components/Form/FormItem';
import Interceptor from '../../../../components/Interceptor';
import triggerPopupAlert from '../../../../components/PopupAlert/trigger';
import SaveButton, { DISABLED, ENABLED, SAVED, SAVING } from '../../../../components/SaveButton';
import ConfigWithCaption from './ConfigWithCaption';
import CustomMappingSection from './CustomMappingSection';
import ResetModal from './ResetModal';
import {
    AUTOMATIC_IMPORTS,
    DEACTIVATE_ACCOUNTS,
    ENABLE_REMOVE_BOSS,
    PASSWORD_EMAIL,
    PREFERRED_NAME,
    PROFILE_PICTURES,
    REACTIVATE_ACCOUNTS,
    SET_BOSS_OF_NEW_USERS,
    SYNC_GENDER,
    UPDATE_BOSSES_OF_EXISTING_USERS,
    UPDATE_EXISTING_USERS,
    USE_COMPANY_HIRING_DATE_FORMAT,
} from './constants';
import { isNonEmptyAndValid, mkCustomMapping, sanitizeSourceString, validateCustomMapping } from './customMapping';

const CSV_DELIMITERS = [
    { value: ',', label: 'Comma (,)' },
    { value: ';', label: 'Semi-colon (;)' },
    { value: '\t', label: 'Tab' },
];

UserImportConfig.propTypes = {
    disabled: PropTypes.bool,
    name: PropTypes.string,
    isPreferredNameAvailable: PropTypes.bool,
    isAutoSendEmailsAvailable: PropTypes.bool,
    isProfilePictureAvailable: PropTypes.bool,
    customMappingConfig: PropTypes.object,
    validationErrorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    hasConnectionDetails: PropTypes.bool.isRequired,
    autoImportHidden: PropTypes.bool,
    customSection: PropTypes.element,
};

function toButtonState({ submitting, pristine, hasValidationErrors }) {
    if (submitting) {
        return SAVING;
    }
    if (hasValidationErrors) {
        return DISABLED;
    }
    if (pristine) {
        return SAVED;
    }
    return ENABLED;
}

export default function UserImportConfig({
    disabled,
    isProfilePictureAvailable,
    isAutoSendEmailsAvailable = true,
    isPreferredNameAvailable,
    customMappingConfig,
    children,
    name,
    config,
    apiImportConfig,
    validationErrorMessage,
    hasConnectionDetails,
    autoImportHidden,
    customSection = <></>,
}) {
    const [showResetConfirmationModal, setShowResetConfirmationModal] = useState(false);
    const onSubmit = async updatedConfig => {
        try {
            await apiImportConfig.saveConfig({
                ...config,
                customMappings: compose(
                    filter(isNonEmptyAndValid),
                    map(sanitizeSourceString)
                )(updatedConfig.customMappings),
                systemSpecificProperties: updatedConfig.systemSpecificProperties,
                useExternalSystemsProfilePictures: updatedConfig.useExternalSystemsProfilePictures ?? false,
                autoSendEmailsOnAdd: updatedConfig.autoSendEmailsOnAdd ?? false,
                mapNicknames: updatedConfig.mapNicknames ?? false,
                deactivateMissingUsers: updatedConfig.deactivateMissingUsers ?? false,
                reactivateAccounts: updatedConfig.reactivateAccounts ?? false,
                synchronizationInterval: updatedConfig.automaticImportsEnabled ? 'EVERY_TWO_HOURS' : 'NEVER',
                syncGender: updatedConfig.syncGender,
                customMappingKeepOriginalValueIfEmpty: updatedConfig.customMappingKeepOriginalValueIfEmpty,
                sftpConfig: updatedConfig.sftpConfig,
                enableRemoveBoss: updatedConfig.enableRemoveBoss ?? false,
                updateExistingUsers: updatedConfig.updateExistingUsers,
                updateBossesOfExistingUsers: updatedConfig.updateBossesOfExistingUsers,
                setBossesOfNewUsers: updatedConfig.setBossesOfNewUsers,
            });
        } catch (error) {
            return {
                [FORM_ERROR]: toErrorMessage(error),
            };
        }
    };

    const toErrorMessage = error => {
        if (error?.response?.data === 'Invalid credentials') {
            return validationErrorMessage;
        } else if (typeof error?.response?.data === 'string') {
            return error.response.data;
        }
        return 'Saving failed.';
    };

    const onReset = async (id, company) => {
        try {
            await apiImportConfig.resetConfig(id, company);
            setShowResetConfirmationModal(false);
        } catch {
            setShowResetConfirmationModal(false);
            triggerPopupAlert('Sorry an error occurred while disabling.', 'error');
            return { [FORM_ERROR]: 'An error occurred while disabling the configuration.' };
        }
    };

    const isAutoImportDisabled =
        config.synchronizingSystem &&
        config.synchronizingSystem.toUpperCase() !== config.externalUserSystem.toUpperCase();

    const customMappings = config.customMappings.length > 0 ? config.customMappings : [mkCustomMapping()];

    return (
        <Card>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    customMappings,
                    systemSpecificProperties: {
                        ...config.systemSpecificProperties,
                        csvDelimiter: name === 'CSV' ? config.systemSpecificProperties.csvDelimiter || ',' : undefined,
                    },
                    useExternalSystemsProfilePictures: config.useExternalSystemsProfilePictures,
                    autoSendEmailsOnAdd: config.autoSendEmailsOnAdd,
                    mapNicknames: config.mapNicknames,
                    deactivateMissingUsers: config.deactivateMissingUsers,
                    reactivateAccounts: config.reactivateAccounts,
                    automaticImportsEnabled: config.synchronizationInterval === 'EVERY_TWO_HOURS',
                    syncGender: config.syncGender,
                    customMappingKeepOriginalValueIfEmpty: config.customMappingKeepOriginalValueIfEmpty,
                    sftpConfig: config.sftpConfig,
                    enableRemoveBoss: config.enableRemoveBoss,
                    updateExistingUsers: config.updateExistingUsers,
                    updateBossesOfExistingUsers: config.updateBossesOfExistingUsers,
                    setBossesOfNewUsers: config.setBossesOfNewUsers,
                }}
                mutators={{
                    ...arrayMutators,
                }}
                validate={values => ({
                    customMappings: map(validateCustomMapping, values.customMappings),
                })}
            >
                {({
                    form,
                    handleSubmit,
                    submitting,
                    pristine,
                    submitError,
                    dirtySinceLastSubmit,
                    hasValidationErrors,
                }) => (
                    <form onSubmit={handleSubmit} className="admin-config">
                        {submitError && !dirtySinceLastSubmit && <AlertBox colour="RED">{submitError}</AlertBox>}
                        <Interceptor if={!pristine} />
                        <fieldset disabled={disabled}>
                            {children}
                            {!autoImportHidden && (
                                <ConfigWithCaption
                                    name="automaticImportsEnabled"
                                    caption={AUTOMATIC_IMPORTS.caption(name)}
                                    label={AUTOMATIC_IMPORTS.label}
                                    isDisabled={isAutoImportDisabled}
                                    isToggle={true}
                                />
                            )}
                            <AccountsSection
                                name={name}
                                isProfilePictureAvailable={isProfilePictureAvailable}
                                isAutoSendEmailsAvailable={isAutoSendEmailsAvailable}
                                isPreferredNameAvailable={isPreferredNameAvailable}
                            />
                            {customSection}
                            <ImportSection name={name} />
                            {customMappingConfig?.enabled && (
                                <CustomMappingSection name={name} config={customMappingConfig} />
                            )}
                            <section>
                                <DeprecatedButtonContainer>
                                    {hasConnectionDetails && (
                                        <Button onClick={() => setShowResetConfirmationModal(true)}>
                                            <IconBan /> Disable Integration
                                        </Button>
                                    )}
                                    <SaveButton
                                        state={toButtonState({
                                            submitting,
                                            hasValidationErrors,
                                            pristine,
                                        })}
                                    />
                                </DeprecatedButtonContainer>
                            </section>
                        </fieldset>
                        <ResetModal
                            isOpen={showResetConfirmationModal}
                            onDelete={() =>
                                onReset(config.id, config.companyId).then(() => {
                                    form.restart();
                                })
                            }
                            onCancel={() => setShowResetConfirmationModal(false)}
                            name={name}
                        />
                    </form>
                )}
            </Form>
        </Card>
    );
}

function AccountsSection({ name, isProfilePictureAvailable, isAutoSendEmailsAvailable, isPreferredNameAvailable }) {
    if (!(isProfilePictureAvailable || isAutoSendEmailsAvailable || isPreferredNameAvailable)) {
        return null;
    }

    return (
        <div>
            <h5>Accounts</h5>

            <FormCheckboxContainer>
                {isProfilePictureAvailable && (
                    <ConfigWithCaption
                        name="useExternalSystemsProfilePictures"
                        caption={PROFILE_PICTURES.caption(name)}
                        label={PROFILE_PICTURES.label(name)}
                    />
                )}

                {isAutoSendEmailsAvailable && (
                    <ConfigWithCaption name="autoSendEmailsOnAdd" label={PASSWORD_EMAIL.label} />
                )}

                {isPreferredNameAvailable && (
                    <ConfigWithCaption
                        name="mapNicknames"
                        caption={PREFERRED_NAME.caption(name)}
                        label={PREFERRED_NAME.label}
                    />
                )}
            </FormCheckboxContainer>
        </div>
    );
}

function ImportSection({ name }) {
    return (
        <div>
            <h5>User Import</h5>

            <FormCheckboxContainer>
                {name === 'Namely' && (
                    <ConfigWithCaption
                        name="syncGender"
                        caption={SYNC_GENDER.caption(name)}
                        label={SYNC_GENDER.label}
                    />
                )}
                {name === 'CSV' && (
                    <>
                        <ConfigWithCaption
                            name="updateExistingUsers"
                            caption={UPDATE_EXISTING_USERS.caption(name)}
                            label={UPDATE_EXISTING_USERS.label}
                        />
                        <ConfigWithCaption
                            name="updateBossesOfExistingUsers"
                            caption={UPDATE_BOSSES_OF_EXISTING_USERS.caption(name)}
                            label={UPDATE_BOSSES_OF_EXISTING_USERS.label}
                        />
                        <ConfigWithCaption
                            name="setBossesOfNewUsers"
                            caption={SET_BOSS_OF_NEW_USERS.caption(name)}
                            label={SET_BOSS_OF_NEW_USERS.label}
                        />
                        <ConfigWithCaption
                            name="systemSpecificProperties.csvUseCompanyFormatForHiringDate"
                            caption={USE_COMPANY_HIRING_DATE_FORMAT.caption(name)}
                            label={USE_COMPANY_HIRING_DATE_FORMAT.label}
                        />
                    </>
                )}
                <ConfigWithCaption
                    name="deactivateMissingUsers"
                    caption={DEACTIVATE_ACCOUNTS.caption(name)}
                    label={DEACTIVATE_ACCOUNTS.label}
                />
                <ConfigWithCaption
                    name="reactivateAccounts"
                    caption={REACTIVATE_ACCOUNTS.caption(name)}
                    label={REACTIVATE_ACCOUNTS.label}
                />
                <ConfigWithCaption
                    name="enableRemoveBoss"
                    caption={ENABLE_REMOVE_BOSS.caption(name)}
                    label={ENABLE_REMOVE_BOSS.label}
                />
                {name === 'CSV' && (
                    <section>
                        <div>
                            <FormItem
                                name="systemSpecificProperties.csvDelimiter"
                                type="select"
                                label="CSV Delimiter"
                                inputComponent={FormSelect}
                                inputProps={{
                                    optionToText: option => CSV_DELIMITERS.find(o => o.value === option).label,
                                    options: CSV_DELIMITERS.map(d => d.value),
                                }}
                            />
                        </div>
                        <aside></aside>
                    </section>
                )}
            </FormCheckboxContainer>
        </div>
    );
}
